/**
 * Strings for debate and rapid-response practice. Every key starts with
 * `rr.`. Spread into both dictionaries in web/src/lib/i18n.ts; the
 * `satisfies` below makes a missing or extra Spanish key a type error here.
 */
export const practiceEn = {
  "rr.title": "Debate practice",
  "rr.subtitle":
    "The hard questions you will be asked, and the answer the campaign agreed on. Say it word for word, then stop.",
  "rr.tab.cards": "Flashcards",
  "rr.tab.drill": "Drill",
  "rr.tab.contrast": "Draw the contrast",
  "rr.empty": "The practice questions haven't been added yet.",
  "rr.emptyBody":
    "Your campaign manager adds the hard questions and the approved answer for each one. Once they are in, you can drill them here between doors.",
  "rr.error": "Something went wrong: {message}",
  "rr.severity.critical": "Critical",
  "rr.severity.medium": "Medium",
  "rr.severity.low": "Low",
  "rr.card.counter": "{n} of {total}",
  "rr.card.hostile": "They ask you",
  "rr.card.tapToFlip": "Tap to see the approved answer",
  "rr.card.tapToReturn": "Tap to see the question",
  "rr.card.approved": "Approved answer",
  "rr.card.workingTranslation": "Working translation",
  "rr.card.englishOriginal": "Approved text (English)",
  "rr.card.guidance": "Guidance",
  "rr.card.keyPoints": "Key points",
  "rr.card.fix": "What the campaign is doing",
  "rr.card.thenStop": "Verbatim, then stop.",
  "rr.grade.prompt": "How did you do?",
  "rr.grade.missed": "Missed",
  "rr.grade.almost": "Almost",
  "rr.grade.got": "Got it",
  "rr.deck.mastered": "{n} mastered",
  "rr.deck.due": "{n} to review",
  "rr.deck.reset": "Reset progress",
  "rr.deck.drillThis": "Drill this one",
  "rr.drill.intro":
    "A hostile question, as it will be asked. Answer out loud or type it; the coach grades against the approved text only.",
  "rr.drill.pickCard": "Topic",
  "rr.drill.random": "Surprise me",
  "rr.drill.another": "Another question",
  "rr.drill.placeholder": "Your answer…",
  "rr.drill.mic": "Speak",
  "rr.drill.micStop": "Stop",
  "rr.drill.listening": "Listening…",
  "rr.drill.micUnsupported": "Voice input is not available in this browser. Type your answer.",
  "rr.drill.micError": "The microphone stopped ({code}).",
  "rr.drill.words": "{n} words · aim for about {target}",
  "rr.drill.submit": "Grade my answer",
  "rr.drill.grading": "Grading…",
  "rr.drill.tryAgain": "Try again",
  "rr.drill.next": "Next question",
  "rr.verdict.on_message": "On message",
  "rr.verdict.partial": "Partial",
  "rr.verdict.off_message": "Off message",
  "rr.verdict.never_use": "Never use",
  "rr.result.score": "score",
  "rr.result.keyPoints": "Key points",
  "rr.result.strongest": "Your strongest line",
  "rr.result.flagged": "Flagged phrases",
  "rr.result.flag.criminal": "Never, in any form",
  "rr.result.flag.avoid": "Avoid (playbook 3.5)",
  "rr.result.flag.prefer": "Say instead: {instead}",
  "rr.result.tone": "Coach's note",
  "rr.result.suggestion": "One fix",
  "rr.result.length": "Length: {ratio}× the approved answer",
  "rr.result.lengthPenalty": "−{n} for running long",
  "rr.result.injection":
    "The answer contained instructions aimed at the grader, so the score was capped.",
  "rr.result.neverTitle": "This never gets said",
  "rr.result.neverBody": "The campaign does not touch that story. If asked, say this, then stop:",
  "rr.result.practiceOnly":
    "Practice, not approval. The server computes the score from the approved text.",
  "rr.result.showApproved": "Compare with the approved answer",
  "rr.history.title": "Recent drills",
  "rr.history.empty": "No drills yet. Your first one is a tap away.",
  "rr.contrast.intro":
    "Every contrast line with where it may be used. Use a line only where its tag says.",
  "rr.contrast.target.all": "All",
  "rr.contrast.target.alluin": "Alluin",
  "rr.contrast.target.guzman": "Guzman",
  "rr.contrast.target.others": "Others",
  "rr.contrast.target.general": "General",
  "rr.clearance.cleared": "Cleared",
  "rr.clearance.westside_only": "Westside only",
  "rr.clearance.runoff_only": "Runoff only",
  "rr.clearance.press_only": "Press & donors only",
  "rr.clearance.never": "Do not use",
  "rr.contrast.evidence": "Evidence",
  "rr.contrast.theirAnswer": "How they will answer",
  "rr.contrast.counter": "Our line",
  "rr.contrast.conditions": "Conditions",
  "rr.contrast.copy": "Copy",
  "rr.contrast.copied": "Copied",
  "rr.contrast.lockedTitle": "Do not use",
  "rr.contrast.lockedBody":
    "Never used — not in mail, digital, a debate, a surrogate's mouth or a whisper. Listed so nobody reaches for them.",
  "rr.contrast.locked": "Locked",
  "rr.contrast.practiceDeflect": "Practice the approved deflection",
  "rr.contrast.changeClearance": "Where it may be used",
  "rr.contrast.empty": "No lines for this filter.",
  "rr.edit.open": "Edit",
  "rr.edit.title": "Edit card · version {n}",
  "rr.edit.responseEn": "Approved answer (English, verbatim)",
  "rr.edit.responseEs": "Spanish translation",
  "rr.edit.approveEs": "The Spanish translation is approved",
  "rr.edit.guidance": "Guidance",
  "rr.edit.fix": "What the campaign is doing",
  "rr.edit.note": "Reason for the change",
  "rr.edit.save": "Save as a new version",
  "rr.edit.saving": "Saving…",
  "rr.edit.saved": "Saved as version {n}.",
  "rr.edit.close": "Close",
  "rr.edit.history": "Version history",
  "rr.edit.historyEmpty": "No earlier versions.",
  "rr.edit.version": "Version {n}",
  "rr.edit.lockedDeflect": "This line is fixed by the playbook and cannot be edited.",
} as const;

export const practiceEs = {
  "rr.title": "Práctica de respuestas",
  "rr.subtitle":
    "Las preguntas difíciles que te van a hacer, y la respuesta que acordó la campaña. Díla palabra por palabra, y luego alto.",
  "rr.tab.cards": "Tarjetas",
  "rr.tab.drill": "Simulacro",
  "rr.tab.contrast": "Marca el contraste",
  "rr.empty": "Todavía no se han agregado las preguntas de práctica.",
  "rr.emptyBody":
    "La gerencia de campaña agrega las preguntas difíciles y la respuesta aprobada de cada una. Cuando estén, podrás practicarlas aquí entre puerta y puerta.",
  "rr.error": "Algo salió mal: {message}",
  "rr.severity.critical": "Crítico",
  "rr.severity.medium": "Medio",
  "rr.severity.low": "Bajo",
  "rr.card.counter": "{n} de {total}",
  "rr.card.hostile": "Te preguntan",
  "rr.card.tapToFlip": "Toca para ver la respuesta aprobada",
  "rr.card.tapToReturn": "Toca para ver la pregunta",
  "rr.card.approved": "Respuesta aprobada",
  "rr.card.workingTranslation": "Traducción de trabajo",
  "rr.card.englishOriginal": "Texto aprobado (inglés)",
  "rr.card.guidance": "Guía",
  "rr.card.keyPoints": "Puntos clave",
  "rr.card.fix": "Lo que hace la campaña",
  "rr.card.thenStop": "Textual, y luego alto.",
  "rr.grade.prompt": "¿Cómo te fue?",
  "rr.grade.missed": "No la sabía",
  "rr.grade.almost": "Casi",
  "rr.grade.got": "La tengo",
  "rr.deck.mastered": "{n} dominadas",
  "rr.deck.due": "{n} por repasar",
  "rr.deck.reset": "Reiniciar progreso",
  "rr.deck.drillThis": "Simulacro con esta",
  "rr.drill.intro":
    "Una pregunta hostil, tal como te la harán. Responde en voz alta o escríbela; el entrenador califica solo contra el texto aprobado.",
  "rr.drill.pickCard": "Tema",
  "rr.drill.random": "Sorpréndeme",
  "rr.drill.another": "Otra pregunta",
  "rr.drill.placeholder": "Tu respuesta…",
  "rr.drill.mic": "Hablar",
  "rr.drill.micStop": "Detener",
  "rr.drill.listening": "Escuchando…",
  "rr.drill.micUnsupported": "La voz no está disponible en este navegador. Escribe tu respuesta.",
  "rr.drill.micError": "El micrófono se detuvo ({code}).",
  "rr.drill.words": "{n} palabras · meta de unas {target}",
  "rr.drill.submit": "Calificar mi respuesta",
  "rr.drill.grading": "Calificando…",
  "rr.drill.tryAgain": "Intentar de nuevo",
  "rr.drill.next": "Siguiente pregunta",
  "rr.verdict.on_message": "En mensaje",
  "rr.verdict.partial": "Parcial",
  "rr.verdict.off_message": "Fuera de mensaje",
  "rr.verdict.never_use": "Nunca usar",
  "rr.result.score": "puntos",
  "rr.result.keyPoints": "Puntos clave",
  "rr.result.strongest": "Tu mejor frase",
  "rr.result.flagged": "Frases señaladas",
  "rr.result.flag.criminal": "Nunca, de ninguna forma",
  "rr.result.flag.avoid": "Evitar (playbook 3.5)",
  "rr.result.flag.prefer": "Mejor di: {instead}",
  "rr.result.tone": "Nota del entrenador",
  "rr.result.suggestion": "Una corrección",
  "rr.result.length": "Largo: {ratio}× la respuesta aprobada",
  "rr.result.lengthPenalty": "−{n} por extenderte",
  "rr.result.injection":
    "La respuesta traía instrucciones dirigidas al calificador, así que la puntuación se limitó.",
  "rr.result.neverTitle": "Esto nunca se dice",
  "rr.result.neverBody": "La campaña no toca ese tema. Si te preguntan, di esto y luego alto:",
  "rr.result.practiceOnly":
    "Práctica, no aprobación. El servidor calcula la puntuación a partir del texto aprobado.",
  "rr.result.showApproved": "Comparar con la respuesta aprobada",
  "rr.history.title": "Simulacros recientes",
  "rr.history.empty": "Aún no hay simulacros. El primero está a un toque.",
  "rr.contrast.intro":
    "Cada línea de contraste con dónde se puede usar. Usa una línea solo donde lo diga su etiqueta.",
  "rr.contrast.target.all": "Todos",
  "rr.contrast.target.alluin": "Alluin",
  "rr.contrast.target.guzman": "Guzman",
  "rr.contrast.target.others": "Otros",
  "rr.contrast.target.general": "General",
  "rr.clearance.cleared": "Aprobada",
  "rr.clearance.westside_only": "Solo Westside",
  "rr.clearance.runoff_only": "Solo segunda vuelta",
  "rr.clearance.press_only": "Solo prensa y donantes",
  "rr.clearance.never": "No usar",
  "rr.contrast.evidence": "Evidencia",
  "rr.contrast.theirAnswer": "Cómo responderán",
  "rr.contrast.counter": "Nuestra línea",
  "rr.contrast.conditions": "Condiciones",
  "rr.contrast.copy": "Copiar",
  "rr.contrast.copied": "Copiado",
  "rr.contrast.lockedTitle": "No usar",
  "rr.contrast.lockedBody":
    "Nunca se usan: ni en correo, ni en digital, ni en un debate, ni en boca de un aliado, ni en voz baja. Están aquí para que nadie los busque.",
  "rr.contrast.locked": "Bloqueada",
  "rr.contrast.practiceDeflect": "Practicar la línea de desvío aprobada",
  "rr.contrast.changeClearance": "Dónde se puede usar",
  "rr.contrast.empty": "No hay líneas con este filtro.",
  "rr.edit.open": "Editar",
  "rr.edit.title": "Editar tarjeta · versión {n}",
  "rr.edit.responseEn": "Respuesta aprobada (inglés, textual)",
  "rr.edit.responseEs": "Traducción al español",
  "rr.edit.approveEs": "La traducción al español está aprobada",
  "rr.edit.guidance": "Guía",
  "rr.edit.fix": "Lo que hace la campaña",
  "rr.edit.note": "Motivo del cambio",
  "rr.edit.save": "Guardar como nueva versión",
  "rr.edit.saving": "Guardando…",
  "rr.edit.saved": "Guardada como versión {n}.",
  "rr.edit.close": "Cerrar",
  "rr.edit.history": "Historial de versiones",
  "rr.edit.historyEmpty": "Sin versiones anteriores.",
  "rr.edit.version": "Versión {n}",
  "rr.edit.lockedDeflect": "Esta línea la fija el playbook y no se puede editar.",
} satisfies Record<keyof typeof practiceEn, string>;
